import React, { useState, useEffect, useRef, useCallback } from 'react';
import PropTypes from 'prop-types';
import { X, ChevronLeft, ChevronRight } from 'lucide-react';
import { useModalDialog } from '../hooks/useModalDialog';
import { useAccessibility } from '../contexts/AccessibilityContext';
import PosterPdfViewer from './PosterPdfViewer';
import './PosterPresentation.css';

/**
 * Full-screen "presentation mode" for browsing poster PDFs one at a time,
 * with Previous/Next buttons and Left/Right arrow keys — lets a delegate
 * flip through posters the way they would at the physical poster boards,
 * instead of opening each one's PDF separately. `posters` should already
 * be filtered to only those with an uploaded poster image/PDF.
 */
const PosterPresentation = ({ posters, startIndex, onClose }) => {
  const [index, setIndex] = useState(startIndex);
  const contentRef = useRef(null);
  const { announce } = useAccessibility();

  const poster = posters[index];
  const multiple = posters.length > 1;

  // Report which poster was on screen at the moment of closing, so the list
  // behind can put focus back on *that* one rather than whichever poster
  // was originally opened — after paging through with Next/Previous, those
  // are usually not the same poster, and landing back at the start of the
  // journey is disorienting when focus is your only cursor.
  // Wrapped rather than passing onClose directly: the close button's
  // onClick would otherwise hand it a click event as its first argument.
  const handleClose = useCallback(() => {
    onClose(poster);
  }, [onClose, poster]);

  useModalDialog(contentRef, { isOpen: true, onClose: handleClose });

  const goPrev = useCallback(() => {
    setIndex((i) => (i - 1 + posters.length) % posters.length);
  }, [posters.length]);

  const goNext = useCallback(() => {
    setIndex((i) => (i + 1) % posters.length);
  }, [posters.length]);

  useEffect(() => {
    if (!poster) return;
    const label = poster.sessionNumber ? `${poster.sessionNumber}: ${poster.title}` : poster.title;
    announce(`${label}, poster ${index + 1} of ${posters.length}`);
  }, [poster, index, posters.length, announce]);

  // Bonus keyboard support alongside the always-reliable Prev/Next buttons.
  useEffect(() => {
    if (!multiple) return undefined;
    const handleKeyDown = (event) => {
      if (event.key === 'ArrowLeft') {
        event.preventDefault();
        goPrev();
      } else if (event.key === 'ArrowRight') {
        event.preventDefault();
        goNext();
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [multiple, goPrev, goNext]);

  if (!poster) return null;

  return (
    <div className="modal-overlay poster-presentation-overlay" role="dialog" aria-modal="true" aria-labelledby="poster-presentation-title">
      <div className="modal-content poster-presentation-content" ref={contentRef} tabIndex={-1}>
        <div className="modal-header">
          <h2 id="poster-presentation-title" className="modal-title">
            {poster.sessionNumber && <span className="poster-presentation-number">{poster.sessionNumber}</span>}
            {poster.title}
          </h2>
          <button className="modal-close-btn" onClick={handleClose} aria-label="Close poster presentation">
            <X size={24} />
          </button>
        </div>

        <div className="poster-presentation-body">
          <button
            type="button"
            className="poster-presentation-nav poster-presentation-nav--prev"
            onClick={goPrev}
            aria-label="Previous poster"
            disabled={!multiple}
          >
            <ChevronLeft size={32} aria-hidden="true" />
          </button>

          <div className="poster-presentation-viewer">
            <PosterPdfViewer
              key={poster.id}
              url={poster.posterImageUrl}
              title={poster.title}
            />
          </div>

          <button
            type="button"
            className="poster-presentation-nav poster-presentation-nav--next"
            onClick={goNext}
            aria-label="Next poster"
            disabled={!multiple}
          >
            <ChevronRight size={32} aria-hidden="true" />
          </button>
        </div>

        {/* Deliberately no "Open in new tab" link here any more. Following
            it left the app entirely, into the browser's own PDF viewer —
            which for AAC/switch-scanning users is a dead end: that tab has
            none of the app's navigation, and switch software often can't
            drive browser tabs or the Back button at all, so there was no
            way back to where they were in the poster list. The viewer now
            zooms and pans in place instead, and Close (or Escape) returns
            focus to exactly the poster they opened. The external link
            still appears, but only if in-app rendering actually fails —
            see PosterPdfViewer.js. */}
        <div className="poster-presentation-footer">
          <span className="poster-presentation-speaker">{poster.speaker || 'Speaker TBA'}</span>
          <span className="poster-presentation-count">{index + 1} of {posters.length}</span>
          <button type="button" className="btn btn-outline" onClick={handleClose}>
            Back to posters
          </button>
        </div>
      </div>
    </div>
  );
};

PosterPresentation.propTypes = {
  posters: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    title: PropTypes.string.isRequired,
    speaker: PropTypes.string,
    sessionNumber: PropTypes.string,
    posterImageUrl: PropTypes.string.isRequired,
  })).isRequired,
  startIndex: PropTypes.number.isRequired,
  onClose: PropTypes.func.isRequired,
};

export default PosterPresentation;
